import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { InfoTooltip } from "./InfoTooltip";

// Ruhezustand des Badges. `active` (Toggle-Zustand) überschreibt den Ton mit Accent, damit
// Callsites nicht `tone={x ? "accent" : "neutral"}` und `active={x}` doppelt führen müssen.
// Bewusst kein Danger-Ton: Rot ist laut UI_CONVENTIONS eine Button-Farbe fuer destruktive
// Aktionen, keine Hervorhebung fuer Zustaende. Ein verfehltes Kriterium wird ueber Glyph und
// Text ausgewiesen, nicht ueber Farbe.
export type KpiBadgeTone = "neutral" | "muted";

const TONE_CLASSES: Record<KpiBadgeTone | "active", { rest: string; hover: string }> = {
  // bg-accent (statt Inline-Style) rebindet --context-ink auf on-accent, dadurch invertieren
  // text-ink/N-Kinder wie das (i)-Icon automatisch. Siehe UI_CONVENTIONS "How text-ink/N adapts".
  active: { rest: "bg-accent", hover: "hover:opacity-90" },
  neutral: { rest: "bg-ink/[0.06] text-ink/70", hover: "hover:text-ink" },
  muted: { rest: "bg-ink/[0.02] text-ink/30", hover: "hover:text-ink" },
};

/**
 * Kompaktes KPI-Badge einer Listenzeile: Label + Wert in einer Pille, optional klickbar
 * (Tab-Switch) und optional mit angehängtem (i)-Tooltip. EIN Ort für Größe, Pill-Form und
 * Farbton, damit Zeilen-KPIs nicht pro Feature neu erfunden werden.
 *
 * Der Tooltip sitzt bewusst als eigener Button neben dem Label-Button, nicht darin: ein
 * Button in einem Button ist ungültiges HTML und macht das Label per Tastatur unerreichbar.
 */
export function KpiBadge({
  tone = "neutral",
  active,
  onClick,
  title,
  info,
  children,
}: {
  tone?: KpiBadgeTone;
  // Opt-in Toggle-Semantik: setzt aria-pressed, damit der aktive Tab auch für Screenreader und
  // im forced-colors-Modus erkennbar ist (der Accent-Zustand allein reicht dort nicht).
  active?: boolean;
  onClick?: (event: ReactMouseEvent) => void;
  title?: string;
  info?: { title: string; ariaLabel: string; width?: string; content: ReactNode };
  children: ReactNode;
}) {
  const { rest, hover } = TONE_CLASSES[active ? "active" : tone];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full py-1.5 text-[12px] tabular-nums ${
        info ? "pl-3 pr-2" : "px-3"
      } ${rest}`}
      title={onClick ? undefined : title}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          title={title}
          aria-pressed={active}
          className={`inline-flex items-center gap-1 rounded-full outline-none transition focus-visible:ring-2 focus-visible:ring-accent/40 ${hover}`}
        >
          {children}
        </button>
      ) : (
        children
      )}
      {info ? (
        <span onClick={(event) => event.stopPropagation()} className="inline-flex">
          <InfoTooltip
            ariaLabel={info.ariaLabel}
            title={info.title}
            placement="bottom"
            className="text-left"
            contentClassName={info.width ?? "w-[22rem] max-w-[22rem]"}
          >
            {info.content}
          </InfoTooltip>
        </span>
      ) : null}
    </span>
  );
}
